<template>
  <v-container fluid class="ip-root pa-4">

    <!-- PAGE HEADER -->
    <div class="ip-header mb-8">
      <div class="ip-header-icon">
        <v-icon size="28" color="white">mdi-package-variant-closed</v-icon>
      </div>
      <div class="ip-header-text">
        <h1 class="ip-title">{{ $t("importarProductos.title") }}</h1>
        <p class="ip-subtitle">
          Subi un archivo con tus productos, configura precios e importalos al sistema
        </p>
      </div>
    </div>

    <!-- CONTENT CARD -->
    <v-card class="ip-card" outlined>
      <v-window v-model="step" touchless class="ip-window">

        <!-- Step 1: Configuracion -->
        <v-window-item :value="1">
          <div class="ip-step-body">
            <div class="ip-step-heading">
              <div class="step-bubble">1</div>
              <div>
                <div class="step-heading-title">{{ $t("importarProductos.step1") }}</div>
                <div class="step-heading-sub">Selecciona el proveedor y el tipo de importacion</div>
              </div>
            </div>
            <div class="ip-step-content">
              <paso-configuracion-inicial ref="paso1" @error="onError" @proveedor-change="paso1ProveedorId = $event" />
            </div>
          </div>
        </v-window-item>

        <!-- Step 2: Mapeo de columnas -->
        <v-window-item :value="2">
          <div class="ip-step-body ip-step-body--wide">
            <div class="ip-step-heading px-8 mb-0">
              <div class="step-bubble">2</div>
              <div>
                <div class="step-heading-title">{{ $t("importarProductos.step2") }}</div>
                <div class="step-heading-sub">Subí el archivo y mapeá cada columna al campo correspondiente</div>
              </div>
            </div>
            <div class="ip-step-content px-8">
              <paso-mapeo-columnas ref="paso2" :modo="modoActual" @error="onError" />
            </div>
          </div>
        </v-window-item>

        <!-- Step 3: Precios y revision (wide) -->
        <v-window-item :value="3">
          <div class="ip-step-body ip-step-body--wide">
            <div class="ip-step-heading px-8 mb-0">
              <div class="step-bubble">3</div>
              <div>
                <div class="step-heading-title">{{ $t("importarProductos.step3") }}</div>
                <div class="step-heading-sub">Revisa precios, codigos de barras y datos antes de importar</div>
              </div>
            </div>
            <div class="ip-step-content pa-0">
              <paso-calculo-precios
                :key="paso3ListaKey"
                ref="paso3"
                :productos="productosParseados"
                :modo="modoActual"
                :validacion="validacion"
                :modo-codigo-barras.sync="modoCodigoBarras"
                @remove="onRemoveProductoPaso3"
              />
            </div>
          </div>
        </v-window-item>

        <!-- Step 4: Confirmar -->
        <v-window-item :value="4">
          <div class="ip-step-body">
            <div class="ip-step-heading">
              <div class="step-bubble step-bubble--success">
                <v-icon small color="white">mdi-check</v-icon>
              </div>
              <div>
                <div class="step-heading-title">{{ $t("importarProductos.step4") }}</div>
                <div class="step-heading-sub">Confirma la importacion de productos al sistema</div>
              </div>
            </div>
            <div class="ip-step-content">
              <paso-confirmar
                :productos-para-enviar="productosParaEnviar"
                :proveedor-nombre="proveedorNombre"
                :modo="modoActual"
              />
            </div>
          </div>
        </v-window-item>

      </v-window>

      <!-- Footer -->
      <v-divider />
      <div class="ip-footer">
        <div class="ip-footer-left">
          <v-btn v-if="step > 1" text color="grey darken-1" @click="goBack">
            <v-icon left small>mdi-arrow-left</v-icon>
            {{ $t("importarProductos.back") }}
          </v-btn>
        </div>
        <div class="ip-footer-right">
          <v-btn
            v-if="step === 1"
            color="primary"
            elevation="0"
            style="min-width: 140px"
            :disabled="!paso1ProveedorId"
            @click="avanzarPaso1"
          >
            {{ $t("importarProductos.continue") }}
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn
            v-if="step === 2"
            color="primary"
            elevation="0"
            :loading="loadingPaso2"
            @click="avanzarPaso2"
          >
            Aplicar mapeo
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn
            v-if="step === 3"
            color="primary"
            elevation="0"
            :loading="loadingPaso4"
            @click="avanzarPaso3"
          >
            Revisar y confirmar
            <v-icon right small>mdi-check-circle-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="step === 4"
            color="success"
            elevation="0"
            :loading="loadingImportar"
            :disabled="!productosParaEnviar.length"
            @click="ejecutarImportacion"
          >
            <v-icon left small>mdi-package-variant-closed</v-icon>
            Importar {{ productosParaEnviar.length ? (productosParaEnviar.length + ' producto' + (productosParaEnviar.length !== 1 ? 's' : '')) : 'productos' }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- SNACKBAR -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarColor === 'error' ? 10000 : 4000" top right>
      <v-icon left small>{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
      <span style="white-space: pre-line;">{{ snackbarText }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import { generarCodigoBarrasPropio } from "../../utils/codigoBarrasUtils";
import PasoConfiguracionInicial from "./PasoConfiguracionInicial.vue";
import PasoMapeoColumnas from "./PasoMapeoColumnas.vue";
import PasoCalculoPrecios from "./PasoCalculoPrecios.vue";
import PasoConfirmar from "./PasoConfirmar.vue";
import ImportarProductosProvider from "../../providers/ImportarProductosProvider";
import ProveedorProvider from "../../../proveedor/providers/ProveedorProvider";
import DepositoProvider from "../../../deposito/providers/DepositoProvider";

export default {
  name: "ImportarProductosPage",
  components: {
    PasoConfiguracionInicial,
    PasoMapeoColumnas,
    PasoCalculoPrecios,
    PasoConfirmar,
  },
  data() {
    return {
      step: 1,
      paso1ProveedorId: null,
      config: null,
      productosParseados: [],
      productosParaEnviar: [],
      validacion: null,
      loadingPaso2: false,
      paso3ListaKey: 0,
      loadingPaso4: false,
      loadingImportar: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      proveedorNombre: "",
      depositoNombre: "",
      modoCodigoBarras: "autogenerar",
    };
  },
  watch: {
    step(newStep, oldStep) {
      if (newStep === 2) this.validacion = null;
      if (oldStep === 2 && newStep === 3) this.validacion = null;
    },
  },
  computed: {
    modoActual() {
      return this.config ? this.config.modo : "SIMPLE";
    },
    tieneErroresValidacion() {
      return this.validacion && this.validacion.totalErrores > 0;
    },
  },
  methods: {
    goBack() {
      if (this.step > 1) this.step--;
    },
    avanzarPaso1() {
      if (!this.$refs.paso1.validate()) return;
      this.config = this.$refs.paso1.getData();
      this.step = 2;
    },
    async avanzarPaso2() {
      const rows = await this.$refs.paso2.procesar();
      if (!rows) return;
      this.loadingPaso2 = true;
      try {
        this.productosParseados = rows;
        this.paso3ListaKey += 1;
        this.step = 3;
      } finally {
        this.loadingPaso2 = false;
      }
    },
    async avanzarPaso3() {
      this.productosParaEnviar = this.$refs.paso3?.getProductosParaEnviar() || [];
      if (!this.productosParaEnviar.length) return;
      this.loadingPaso4 = true;
      try {
        await this.cargarValidacion();
        if (this.validacion && this.validacion.totalErrores > 0) return;
        this.proveedorNombre = await this.getProveedorNombre(this.config.proveedorId);
        this.depositoNombre = await this.getDepositoNombre(this.config.depositoId);
        this.step = 4;
      } finally {
        this.loadingPaso4 = false;
      }
    },
    getCodigoBarrasParaValidacion(p) {
      const codigoProveedor =
        p.codigoBarrasProveedor != null && String(p.codigoBarrasProveedor).trim() !== ""
          ? String(p.codigoBarrasProveedor).trim()
          : (p.codigoBarras || "");
      const overridePropio = (p.codigoBarrasPropio != null ? String(p.codigoBarrasPropio) : "").trim();
      if (overridePropio) return overridePropio;
      if (this.modoCodigoBarras === "autogenerar") {
        const modeloOrNombre = this.config?.modo === "SIMPLE" ? p.nombre : p.modelo;
        const codigoPropio = generarCodigoBarrasPropio(p.marca, p.tamanio, codigoProveedor, modeloOrNombre);
        return codigoPropio || codigoProveedor;
      }
      return codigoProveedor;
    },
    async cargarValidacion(opts = {}) {
      const silent = opts.silent === true;
      if (!this.config || !this.productosParaEnviar.length) return;
      const callerManagesLoading = this.loadingPaso4;
      if (!silent && !callerManagesLoading) this.loadingPaso4 = true;
      try {
        const rows = this.productosParaEnviar.map((p) => ({
          codigoBarras: this.getCodigoBarrasParaValidacion(p),
          nombre: p.nombre,
          marca: p.marca,
          modelo: p.modelo,
          tamanio: p.tamanio,
          otrasCaracteristicas: p.otrasCaracteristicas,
          precioCompra: p.precioCompra,
          stock: 0,
          descuentoProveedor: p.descuentoProveedor != null ? p.descuentoProveedor : undefined,
        }));
        const res = await ImportarProductosProvider.validar(
          this.config.proveedorId,
          this.config.depositoId,
          this.config.modo,
          rows
        );
        this.validacion = res?.data?.productoImportarValidar || null;
      } catch (e) {
        this.onError(e.message || "Error al validar");
      } finally {
        if (!silent && !callerManagesLoading) this.loadingPaso4 = false;
      }
    },
    async onRemoveProductoPaso3() {
      await this.$nextTick();
      this.productosParaEnviar = this.$refs.paso3?.getProductosParaEnviar() || [];
      this.validacion = null;
    },
    async ejecutarImportacion() {
      if (!this.config || !this.productosParaEnviar.length) {
        this.onError("No hay productos para importar");
        return;
      }
      const modoCodigoBarras = this.modoCodigoBarras;
      if (modoCodigoBarras === "manual") {
        const faltantes = this.productosParaEnviar.filter((p) => !(p.codigoBarrasPropio != null && String(p.codigoBarrasPropio).trim()));
        if (faltantes.length > 0) {
          this.onError("En modo manual, complete el codigo propio en cada fila.");
          return;
        }
      }
      const productos = this.productosParaEnviar.map((p) => {
        const codigoProveedor =
          p.codigoBarrasProveedor != null && String(p.codigoBarrasProveedor).trim() !== ""
            ? String(p.codigoBarrasProveedor).trim()
            : (p.codigoBarras || "");
        const overridePropio = (p.codigoBarrasPropio != null ? String(p.codigoBarrasPropio) : "").trim();
        let codigoBarras;
        if (overridePropio) {
          codigoBarras = overridePropio;
        } else if (modoCodigoBarras === "autogenerar") {
          const modeloOrNombre = this.config.modo === "SIMPLE" ? p.nombre : p.modelo;
          codigoBarras = generarCodigoBarrasPropio(p.marca, p.tamanio, codigoProveedor, modeloOrNombre) || codigoProveedor;
        } else {
          codigoBarras = codigoProveedor;
        }
        const codigoBarrasProveedorVal = (p.codigoBarrasProveedor != null && String(p.codigoBarrasProveedor).trim() !== "")
          ? String(p.codigoBarrasProveedor).trim()
          : undefined;
        return {
          codigoBarras,
          codigoBarrasProveedor: codigoBarrasProveedorVal,
          nombre: p.nombre,
          marca: p.marca,
          modelo: p.modelo,
          tamanio: p.tamanio,
          otrasCaracteristicas: p.otrasCaracteristicas || "",
          precioCompra: p.precioCompra,
          stock: 0,
          stockAlerta: 0,
          descuentoProveedor: p.descuentoProveedor != null ? p.descuentoProveedor : 0,
          ivaPorcentaje: p.ivaPorcentaje != null ? p.ivaPorcentaje : 0,
          porcentajeSobreCosto: p.porcentajeSobreCosto != null ? p.porcentajeSobreCosto : 0,
          precioVenta: p.precioVenta != null ? p.precioVenta : p.precioCompra,
        };
      });
      this.loadingImportar = true;
      try {
        const input = {
          proveedorId: this.config.proveedorId,
          depositoId: this.config.depositoId,
          modo: this.config.modo,
          productos,
        };
        const res = await ImportarProductosProvider.importarBulk(input);
        const result = res?.data?.productoImportarBulk;
        if (result?.ok) {
          const erroresStr = result?.errores?.length
            ? "\n\nErrores en algunas filas:\n" + result.errores.join("\n")
            : "";
          this.onSuccess((result.mensaje || this.$t("importarProductos.success")) + erroresStr);
        } else {
          const msg = this.formatErroresImportacion(result?.errores) || result?.mensaje || this.$t("importarProductos.error");
          this.onError(msg);
        }
      } catch (e) {
        const msg = e?.graphQLErrors?.[0]?.message || e?.message || this.$t("importarProductos.error");
        this.onError(msg);
      } finally {
        this.loadingImportar = false;
      }
    },
    async getProveedorNombre(id) {
      if (!id) return "";
      try {
        const res = await ProveedorProvider.findProveedor(id);
        return res?.data?.proveedorFind?.nombre || "";
      } catch { return ""; }
    },
    async getDepositoNombre(id) {
      if (!id) return "";
      try {
        const res = await DepositoProvider.findDeposito(id);
        return res?.data?.depositoFind?.nombre || "";
      } catch { return ""; }
    },
    formatErroresImportacion(errores) {
      if (!errores || !errores.length) return null;
      return errores
        .map((e) => {
          let msg = String(e)
            .replace(/validation\.unique/g, "ya existe en el sistema")
            .replace(/validation failed/gi, "error de validacion");
          return msg;
        })
        .join("\n");
    },
    onSuccess(msg) {
      this.snackbarText = msg || this.$t("importarProductos.success");
      this.snackbarColor = "success";
      this.snackbar = true;
      this.$router.push({
        name: "ProductoManagementPage",
        query: this.config?.depositoId ? { depositoId: this.config.depositoId } : undefined,
      });
    },
    onError(msg) {
      this.snackbarText = msg || this.$t("importarProductos.error");
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* ========================
   ROOT
======================== */
.ip-root {
  max-width: 100%;
}

/* ========================
   HEADER
======================== */
.ip-header {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ip-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.32);
}
.ip-title {
  font-size: 22px;
  font-weight: 700;
  color: #202124;
  margin: 0 0 3px;
  line-height: 1.2;
}
.ip-subtitle {
  font-size: 13.5px;
  color: #5F6368;
  margin: 0;
}

/* ========================
   CONTENT CARD
======================== */
.ip-card {
  border-radius: 14px !important;
  overflow: hidden;
  border-color: #E8EAED !important;
}
.ip-window {
  min-height: 420px;
}

/* Step body */
.ip-step-body {
  padding: 32px 36px 28px;
}
.ip-step-body--wide {
  max-width: 100%;
  padding: 24px 0 0;
}

/* Step heading */
.ip-step-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 28px;
}
.step-bubble {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1976D2;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}
.step-bubble--success {
  background: #2E7D32;
}
.step-heading-title {
  font-size: 16px;
  font-weight: 700;
  color: #202124;
  line-height: 1.3;
}
.step-heading-sub {
  font-size: 12.5px;
  color: #5F6368;
  margin-top: 3px;
}

/* Step content */
.ip-step-content {
  padding-left: 44px;
}
.ip-step-body--wide .ip-step-content {
  padding: 0;
}

/* ========================
   FOOTER
======================== */
.ip-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #FAFAFA;
}
.ip-footer-left,
.ip-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
